import React, { useMemo, useState } from "react";
import { useLocationUpdates } from "@/hooks/useLocationUpdates";
import { Location, Participant, TripUser } from "@/types";
import { formatDistanceToNow } from "date-fns";
import {
  Bed,
  Calendar,
  Users,
  Trash2,
  PencilLine,
  Plus,
  X,
  LucideIcon,
  Plane,
  PlusCircle,
} from "lucide-react";
import { BellIcon, BellSlashIcon } from "@heroicons/react/24/solid";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useAuthStore from "@/stores/user/authStore";
import useView from "@/stores/tripBuilder/useView";
import api from "@/config/axiosConfig";
import { motion } from "framer-motion";

// New interfaces for different update types
interface BaseMetadata {
  itemName?: string;
  image?: string;
}

interface ExperienceMetadata extends BaseMetadata {
  duration?: string;
  rating?: number;
  price?: string;
}

interface AccommodationMetadata extends BaseMetadata {
  checkIn?: string;
  checkOut?: string;
  rating?: number;
}

interface TransportMetadata extends BaseMetadata {
  departureTime?: string;
  arrivalTime?: string;
  type?: string; // flight, train, etc
}

interface ParticipantsMetadata {
  participants: string[];
  added: boolean;
}

type UpdateMetadata =
  | ExperienceMetadata
  | AccommodationMetadata
  | TransportMetadata
  | ParticipantsMetadata;

interface LocationUpdate {
  id: string;
  type: string;
  user_id: string;
  created_at: string;
  metadata: UpdateMetadata;
}

interface LocationUpdatesProps {
  location: Location | null;
}

const getUpdateIcon = (type: string): LucideIcon => {
  if (type.includes("accommodation")) return Bed;
  if (type.includes("transport")) return Plane;
  if (type.includes("experience")) return Calendar;
  if (type.includes("participants")) return Users;
  return PencilLine;
};

const getActionIcon = (type: string): LucideIcon => {
  if (type.includes("removed")) return Trash2;
  if (type.includes("updated")) return PencilLine;
  if (type.includes("added")) return Plus;
  return PencilLine;
};

const dismissUpdate = async (updateId: string) => {
  try {
    await api.social.post(`/location-updates/dismiss`, {
      update_id: updateId,
      user_id: useAuthStore.getState().user?.user_id,
    });
  } catch (error) {
    console.error(error);
  }
};

const LocationUpdates: React.FC<LocationUpdatesProps> = ({ location }) => {
  const { data: updates, isLoading } = useLocationUpdates(location);
  const { tripData } = useTripStore();
  const { setActiveTab } = useView();

  const [dismissedUpdates, setDismissedUpdates] = useState<Set<string>>(
    new Set()
  );

  // create map of users and their details
  const usersMap: Record<string, TripUser> = useMemo(() => {
    return (
      tripData!.users.reduce((acc, user) => {
        acc[user.user_id] = user;
        return acc;
      }, {} as Record<string, TripUser>) ?? {}
    );
  }, [tripData]);

  const handleDismiss = async (updateId: string) => {
    try {
      setDismissedUpdates((prev) => new Set([...prev, updateId]));
      await dismissUpdate(updateId);
    } catch (error) {
      console.error("Failed to dismiss update:", error);
    }
  };

  const handleAddActivities = () => {
    setActiveTab("schedule");
  };

  const renderUpdateContent = (update: LocationUpdate) => {
    if (dismissedUpdates.has(update.id)) return null;

    const UpdateIcon = getUpdateIcon(update.type);
    const userDetails = usersMap[update.user_id];
    const metadata = update.metadata as any;

    return (
      <motion.div
        key={update.id}
        initial={{ opacity: 1, x: 0, zIndex: 1 }}
        exit={{
          opacity: 0,
          x: -100,
          zIndex: 0,
          transition: {
            duration: 0.4,
            opacity: { duration: 0.3 },
          },
        }}
        layout
        className="bg-white rounded-lg shadow-sm px-3 2xl:px-5 pt-4 2xl:pt-6 pb-2 2xl:pb-4 flex flex-col relative"
      >
        <div className="flex justify-between items-start relative">
          <div className="flex items-start">
            <div
              className={`w-8 h-8 2xl:w-10 2xl:h-10 shrink-0 rounded-full flex items-center justify-center mr-3 2xl:mr-5 ${
                update.type.includes("accommodation")
                  ? "bg-blue-50"
                  : update.type.includes("transport")
                  ? "bg-pink-50"
                  : update.type.includes("participants")
                  ? "bg-orange-50"
                  : "bg-indigo-50"
              }`}
            >
              {" "}
              <UpdateIcon
                className={`w-4 h-4 2xl:w-5 2xl:h-5 ${
                  update.type.includes("accommodation")
                    ? "text-blue-600"
                    : update.type.includes("transport")
                    ? "text-pink-600"
                    : update.type.includes("experience")
                    ? "text-purple-600"
                    : update.type.includes("participants")
                    ? "text-orange-600"
                    : "text-indigo-600"
                }`}
              />
            </div>
            <div className="flex flex-col space-y-1 2xl:space-y-2">
              <h3 className="text-[13px] 2xl:text-[14px]">
                <span className="font-normal">{userDetails?.first_name}</span>{" "}
                <span className="text-neutral-500 font-light">
                  {getUpdateMessage(update.type, metadata, usersMap)}
                </span>
              </h3>
              <h4 className="text-[11px] 2xl:text-[12px] font-light text-neutral-400">
                {formatDistanceToNow(new Date(update.created_at), {
                  addSuffix: true,
                })}
              </h4>
            </div>
          </div>
          <button
            onClick={() => handleDismiss(update.id)}
            className="absolute -bottom-2 right-0 p-1 hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 rounded-full transition-colors duration-200 translate-x-1 -translate-y-2"
          >
            <X className="w-3 h-3 2xl:w-4 2xl:h-4" />
          </button>
        </div>
      </motion.div>
    );
  };

  const isLoadingContent = () => {
    return (
      <div className="flex flex-col overflow-y-auto no-scrollbar rounded-lg space-y-2 h-full py-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{
              repeat: Infinity,
              duration: 1.5,
              ease: "easeInOut",
            }}
            className="bg-white rounded-lg shadow-sm px-3 pt-4 pb-2 flex flex-col relative"
          >
            <div className="flex justify-between items-start relative">
              <div className="flex items-start w-full">
                <div className="w-8 h-8 bg-neutral-100 rounded-full shrink-0 mr-3" />
                <div className="flex flex-col space-y-1 w-full">
                  <div className="h-5 bg-neutral-200 rounded-md w-4/5" />
                  <div className="h-3 bg-neutral-200 rounded-md w-1/3" />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  const visibleUpdates =
    updates?.filter((update) => !dismissedUpdates.has(update.id)) || [];

  return (
    <div
      className="w-full h-full bg-neutral-50 2xl:bg-neutral-100 rounded-xl flex flex-col p-2 2xl:p-2.5 border-[1px] border-neutral-100 ring-1 ring-neutral-100 shadow overflow-hidden"
      data-tutorial="overview-notifications"
    >
      {/* Header - Fixed Height */}
      <div className="shrink-0 flex flex-row justify-between w-full items-center h-10 2xl:h-11 px-1 2xl:px-1.5">
        <div className="flex flex-row items-center justify-between w-full">
          <div className="flex flex-row items-center pr-3 2xl:pr-4 space-x-2 ring-1 ring-neutral-200 rounded-full h-8 2xl:h-9">
            <div className="flex items-center justify-center w-8 h-8 2xl:w-9 2xl:h-9 bg-indigo-800 rounded-full">
              <BellIcon className="w-4 h-4 2xl:w-[17px] 2xl:h-[17px] text-white" />
            </div>
            <p className="text-sm 2xl:text-[15px] font-light text-neutral-700">
              Recent Updates
            </p>
          </div>
          <div className="w-6 h-6 2xl:w-8 2xl:h-8 bg-indigo-100 rounded-full text-xs 2xl:text-sm font-medium text-indigo-500 flex items-center justify-center">
            {visibleUpdates.length || 0}
          </div>
        </div>
      </div>

      {/* Content Container - Scrollable */}
      <div className="grow min-h-0 overflow-hidden">
        {isLoading ? (
          isLoadingContent()
        ) : (
          <div className="h-full overflow-y-auto no-scrollbar rounded-lg space-y-2">
            {updates && updates.length && visibleUpdates.length > 0 ? (
              <div className="overflow-hidden space-y-2 py-2">
                {visibleUpdates.map((update) => renderUpdateContent(update))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="h-full rounded-lg flex flex-col items-center justify-center space-y-4"
              >
                <BellSlashIcon className="w-6 h-6 2xl:w-7 2xl:h-7 text-neutral-400" />
                <p className="text-[13.5px] 2xl:text-[15px] text-neutral-400 font-light">
                  No updates for {location?.name}
                </p>
                <button
                  onClick={handleAddActivities}
                  className="inline-flex items-center space-x-1.5 text-white hover:bg-indigo-800 bg-indigo-700 rounded-full px-3 2xl:px-4 py-2 text-[12px] 2xl:text-[13px] font-medium transition-colors duration-150"
                >
                  <PlusCircle className="w-4 h-4 2xl:w-[18px] 2xl:h-[18px]" />
                  <span>Add Activities</span>
                </button>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function getUpdateMessage(
  type: string,
  metadata: any,
  usersMap: Record<string, TripUser>
): string {
  switch (type) {
    case "experience_added":
      if ("rating" in metadata) {
        if (!metadata.itemName.endsWith("tour")) {
          return `added ${metadata.itemName} tour`;
        }
        return `added ${metadata.itemName} experience`;
      } else {
        return `added ${metadata.itemName} to the trip`;
      }
    case "experience_removed":
      return `removed ${metadata.itemName}`;
    case "experience_updated":
      return `updated ${metadata.itemName}`;
    case "accommodation_added":
      return `added ${metadata.lodging.name}`;
    case "accommodation_removed":
      return `removed ${metadata.lodging.name}`;
    case "accommodation_updated":
      return `updated accommodation details`;
    case "transport_added":
      if (metadata.itemType === "flight") {
        return `added a flight to ${metadata.itemName}`;
      }
      return `added transportation details`;
    case "transport_removed":
      return `removed transportation details`;
    case "transport_updated":
      return `updated transportation details`;
    case "participants_changed": {
      if (!metadata.participants?.length) {
        return metadata.added
          ? "added participants to the location"
          : "removed participants from the location";
      }

      const currentUserId = useAuthStore.getState().user?.user_id;

      const participantNames = metadata.participants
        .map((participant: Participant) => {
          if (participant.id === currentUserId) {
            return "themselves";
          }
          return participant.name || "Unknown User";
        })
        .join(", ");

      return metadata.added
        ? `added ${participantNames} to the location`
        : `removed ${participantNames} from the location`;
    }
    default:
      return "made changes to the location";
  }
}

export default LocationUpdates;
